import type { SchoolLevel } from '@jeraisonne/db';
import { SWIPE_THRESHOLD_PX } from '@/features/decouverte/carousel-nav';
import type { DecouverteProof, DecouverteYearStop } from '@/features/decouverte/decouverte-slides';
import { LEVEL_LABEL } from '@/features/demo/types';

/**
 * Les arguments du dernier écran de la démo, qui défilent tout seuls : ce qui attend l'élève une
 * fois le compte créé. Données PURES (le JSX des visuels vit dans `fun-screen.tsx`).
 *
 * Du plus SÉRIEUX au plus fun (le programme d'abord, le live à la fin) : à la sortie d'un exercice,
 * ce qui rassure vient avant ce qui amuse.
 *
 * Chaque argument montre un VISUEL — les mêmes « preuves » que l'accueil `/decouverte`
 * (SKILLCARDS, classement d'un live, frise de l'année, bilan) plus deux propres à cet écran (un
 * défi privé, une fiche de révision). Aucun prix : on vend l'inscription, pas un abonnement.
 */

export type FunVisual = DecouverteProof | { kind: 'challenge' } | { kind: 'revision' };

export interface FunSlide {
  id: string;
  eyebrow: string;
  title: string;
  /** Fin du titre, mise en couleur. */
  highlight: string;
  body: string;
  visual: FunVisual;
}

/** Temps d'affichage d'un argument avant de passer au suivant. */
export const FUN_SLIDE_MS = 3800;

/** Les quatre repères de la frise : vrais au collège comme au lycée (le badge porte la classe). */
const FUN_YEAR: readonly DecouverteYearStop[] = [
  { month: 'Sept', title: 'Rentrée', state: 'done' },
  { month: 'Nov', title: 'Ton pack du moment', state: 'now' },
  { month: 'Janv', title: 'La suite', state: 'next' },
  { month: 'Juin', title: 'Tout le programme', state: 'goal' },
];

export function funSlides(level: SchoolLevel | null): readonly FunSlide[] {
  return [
    {
      id: 'programme',
      eyebrow: 'Ton programme',
      title: 'Tout le programme,',
      highlight: 'pack par pack.',
      body: 'Mois par mois, ce qu’il faut savoir — et où tu en es.',
      visual: {
        kind: 'year',
        badge: level ? `Ton programme · ${LEVEL_LABEL[level]}` : 'Ton programme',
        stops: FUN_YEAR,
      },
    },
    {
      id: 'diagnostic',
      eyebrow: 'Diagnostic gratuit',
      title: '5 minutes pour savoir',
      highlight: 'où tu en es.',
      body: 'Une quinzaine de questions, et tu vois ce qui est acquis.',
      visual: {
        kind: 'list',
        title: 'Ton diagnostic',
        rows: [
          { icon: '✅', label: 'Calcul littéral', detail: 'Acquis', tone: 'good' },
          { icon: '⚠️', label: 'Pythagore', detail: 'À revoir', tone: 'warn' },
          { icon: '✅', label: 'Fractions', detail: 'Acquis', tone: 'good' },
        ],
      },
    },
    {
      id: 'skillcards',
      eyebrow: 'SKILLCARDS',
      title: 'Chaque méthode',
      highlight: 'devient une carte.',
      body: 'Tu les collectionnes et tu fais monter leurs HORA.',
      visual: { kind: 'cards', chip: '+40 HORA' },
    },
    {
      id: 'fiche',
      eyebrow: 'Fiche de révision',
      title: 'Tes fiches,',
      highlight: 'prêtes avant le contrôle.',
      body: 'La règle, un exemple, le piège : une fiche par pack.',
      visual: { kind: 'revision' },
    },
    {
      id: 'partout',
      eyebrow: 'Partout avec toi',
      title: 'Révise où tu veux,',
      highlight: 'quand tu veux.',
      body: 'Dans le bus, à la maison : 5 minutes suffisent.',
      visual: {
        kind: 'list',
        title: 'Partout avec toi',
        rows: [
          { icon: '📱', label: 'Sur ton téléphone', tone: 'info' },
          { icon: '💻', label: 'Sur ton ordi', tone: 'info' },
          { icon: '⏱️', label: '5 minutes suffisent', tone: 'gold' },
        ],
      },
    },
    {
      id: 'defis',
      eyebrow: 'Défis privés',
      title: 'Défie tes parents',
      highlight: 'et tes amis.',
      body: 'Tu lances le défi, ils jouent avec toi, en direct.',
      visual: { kind: 'challenge' },
    },
    {
      id: 'live',
      eyebrow: 'Live School',
      title: 'Chaque dimanche,',
      highlight: 'la plus grande classe.',
      body: 'Les mêmes questions pour tous les élèves de ta classe, et une SKILLCARD légendaire à gagner.',
      visual: { kind: 'live' },
    },
  ];
}

/** Index ramené dans la liste, en BOUCLE (le défilement ne s'arrête jamais sur le dernier). */
export function loopIndex(index: number, count: number): number {
  if (count <= 0) return 0;
  return ((index % count) + count) % count;
}

/**
 * Index cible après un glissé horizontal, en boucle.
 * `deltaX` négatif = doigt vers la gauche = argument suivant.
 */
export function funSwipe(
  deltaX: number,
  index: number,
  count: number,
  threshold: number = SWIPE_THRESHOLD_PX,
): number {
  if (count <= 0) return 0;
  if (deltaX <= -threshold) return loopIndex(index + 1, count);
  if (deltaX >= threshold) return loopIndex(index - 1, count);
  return loopIndex(index, count);
}
